import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetMany, useTranslate } from 'react-admin';
import keyBy from 'lodash/keyBy';
import {
  Box,
  Button,
  CircularProgress,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import MuiDialog from '../../../../base/components/mui/MuiDialog';
import { PaymentContext, usePayInvoices, usePayment } from '../../hooks';
import resourceSlug from '../../../../constant/resource-slug';
import InvoiceTable from './InvoiceTable';
import { PAYMENT_CHANNEL, PAYMENT_METHOD } from '../../../../constant/invoice';
import { formatMoney } from '../../../../services/util/formatNumber';
import PaymentChannels from './PaymentChannels';
import { showConfirmDialog } from '../../../../services/redux/app/app.actions';
import PaymentTransfer from './PaymentTransfer';
import { hasPermission, isSystemUsers } from '../../../../services/util/auth';
import PaymentPayslip, { validatePayslip } from './PaymentPayslip';
import { getBackofficeEnv } from '../../../../services/util';

const PaymentModal = ({ onSuccess, invoiceIds, setInvoiceIds }) => {
  const classes = useStyles();
  const t = useTranslate();
  const isSU = isSystemUsers();
  const { MINIMUM_DEPOSIT_AMOUNT } = getBackofficeEnv();
  const hasCreatePaymentPermission = hasPermission(resourceSlug.PAYMENT, 'create');
  const [activeStep, setActiveStep] = useState(0);
  const [payslip, setPayslip] = useState({});
  const [paymentData, setPaymentData] = useState({});
  const [paymentChannel, setPaymentChannel] = useState(PAYMENT_CHANNEL.USDT_ERC);
  const [invoices, setInvoices] = useState();

  const { data, loading, loaded, error } = useGetMany(resourceSlug.INVOICE, invoiceIds, {
    enabled: !!invoiceIds?.length,
  });

  const open = !!invoiceIds?.length;

  const [totalPayableAmount, currencyCode] = useMemo(
    () =>
      Object.values(invoices || {}).reduce(
        ([sum], invoice) => [sum + invoice.invoiceAmount, invoice.currencyCode],
        [0, ''],
      ),
    [invoices],
  );

  const isEnoughAmount = useMemo(() => {
    if (!totalPayableAmount) return false;

    if (isSU && hasCreatePaymentPermission) return true;

    return !!totalPayableAmount && totalPayableAmount >= MINIMUM_DEPOSIT_AMOUNT;
  }, [totalPayableAmount, isSU, hasCreatePaymentPermission]);

  useEffect(() => {
    if (data) {
      setInvoices(
        keyBy(
          data
            .filter((item) => !!item)
            .map((item) => ({
              ...item,
              ...item.invoice,
            })) || [],
          'id',
        ),
      );
    }
  }, [data]);

  useEffect(() => {
    if (invoices && Object.values(invoices).length === 0) {
      setInvoiceIds([]);
    }
  }, [invoices]);

  useEffect(() => {
    if (!open) {
      setPaymentChannel(PAYMENT_CHANNEL.USDT_ERC);
      setPaymentData({});
      setActiveStep(0);
      setInvoices();
      setPayslip({});
    }
  }, [open]);

  return (
    <PaymentContext.Provider
      value={{
        activeStep,
        setActiveStep,
        invoices,
        paymentChannel,
        setPaymentChannel,
        paymentData,
        setPaymentData,
        setInvoices,
        invoiceIds,
        loading,
        loaded,
        error,
        totalPayableAmount,
        payslip,
        setPayslip,
        currencyCode,
        onSuccess,
        setInvoiceIds,
        isEnoughAmount,
      }}
    >
      <MuiDialog
        open={open}
        onClose={() => setInvoiceIds([])}
        title={t('resources.payment.name')}
        actions={<PaymentToolbar />}
        className={classes.dialog}
      >
        <PayStepper />
        {activeStep === 0 && <InvoiceTable />}
        {activeStep === 1 && (isSU ? <PaymentPayslip /> : <PaymentChannels />)}
        {activeStep === 2 && <PaymentTransfer />}
      </MuiDialog>
    </PaymentContext.Provider>
  );
};

export default PaymentModal;

const PayStepper = memo(() => {
  const classes = useStyles();
  const t = useTranslate();
  const { activeStep } = usePayment();
  const isSU = isSystemUsers();

  const steps = isSU
    ? [t('resources.invoice.paymentAmount'), t('resources.invoice.uploadPayslip')]
    : [
        t('resources.invoice.paymentAmount'),
        t('resources.invoice.paymentChannels'),
        t('resources.invoice.paymentTransfer'),
      ];

  return (
    <Stepper className={classes.root} activeStep={activeStep}>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
});

const PaymentToolbar = memo(() => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const t = useTranslate();
  const isSU = isSystemUsers();

  const {
    activeStep,
    setActiveStep,
    invoices = {},
    payslip,
    setPayslip,
    setPaymentData,
    paymentChannel,
    setInvoiceIds,
    onSuccess,
    isEnoughAmount,
  } = usePayment();

  const { generatePaymentQA, isGeneratingQR } = usePayInvoices({
    onSuccess: (paymentData) => {
      if (isSU) {
        onSuccess?.();
        setInvoiceIds([]);
      } else {
        setPaymentData(paymentData);
        setActiveStep(2);
      }
    },
  });

  const handleNextStep = useCallback(() => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (isSU) {
        const errors = validatePayslip(payslip, t);
        if (errors) {
          setPayslip({
            ...payslip,
            errors,
          });
          return;
        }
        dispatch(
          showConfirmDialog({
            isOpen: true,
            title: 'resources.invoice.paidTitle',
            content: 'resources.invoice.paidMessage',
            onConfirm: () => {
              generatePaymentQA({
                invoiceIds: Object.keys(invoices),
                paymentMethod: PAYMENT_METHOD.OFFLINE_PAYMENT,
                description: payslip.description,
                paymentProofKeys: payslip.paymentProofKeys,
              });
            },
          }),
        );
      } else {
        generatePaymentQA({
          invoiceIds: Object.keys(invoices),
          paymentChannel,
          paymentMethod: PAYMENT_METHOD.USDT,
        });
      }
    } else if (activeStep === 2) {
      dispatch(
        showConfirmDialog({
          isOpen: true,
          title: 'resources.invoice.paidTitle',
          content: 'resources.invoice.paidMessage',
          onConfirm: () => {
            onSuccess?.();
            setInvoiceIds([]);
          },
        }),
      );
    }
  }, [activeStep, invoices, setActiveStep, paymentChannel, setInvoiceIds, onSuccess, isSU, payslip, t, setPayslip]);

  return (
    <Box display="flex" justifyContent="space-between" py="2px" width="100%">
      {activeStep > 0 && (
        <Button variant="outlined" disableElevation onClick={() => setActiveStep(activeStep - 1)}>
          {t('ra.action.back')}
        </Button>
      )}
      <Box
        display="flex"
        alignItems="center"
        ml="auto"
        sx={{
          gap: 12,
        }}
      >
        <PaymentToolbarDescription />
        <Button
          variant="contained"
          disableElevation
          onClick={handleNextStep}
          color="primary"
          disabled={isGeneratingQR || Object.keys(invoices).length < 1 || (activeStep === 0 && !isEnoughAmount)}
          startIcon={isGeneratingQR && <CircularProgress size={18} color={theme.palette.grey[500]} />}
        >
          {activeStep === 2 || (activeStep === 1 && isSU) ? t('ra.action.done') : t('ra.action.next')}
        </Button>
      </Box>
    </Box>
  );
});

const PaymentToolbarDescription = memo(() => {
  const theme = useTheme();
  const t = useTranslate();
  const { activeStep, totalPayableAmount, currencyCode, isEnoughAmount } = usePayment();

  const { MINIMUM_DEPOSIT_AMOUNT } = getBackofficeEnv();

  if (activeStep === 0 && totalPayableAmount) {
    return isEnoughAmount ? (
      <Box
        display="flex"
        fontWeight={600}
        sx={{
          gap: 4,
        }}
      >
        {t('resources.invoice.totalPayableAmount')}
        <Typography variant="h4">{formatMoney(totalPayableAmount, currencyCode)}</Typography>
      </Box>
    ) : (
      <Box
        display="flex"
        fontWeight={600}
        color={theme.palette.error.main}
        textAlign="right"
        sx={{
          gap: 4,
          fontSize: 14,
          lineHeight: '15px',
          whiteSpace: 'pre',
        }}
      >
        {t('resources.invoice.requiredMinPayableAmount', {
          minAmount: formatMoney(MINIMUM_DEPOSIT_AMOUNT, currencyCode),
        })}
      </Box>
    );
  }

  if (activeStep === 2) {
    return (
      <Box
        display="flex"
        fontSize="italic"
        alignItems="center"
        color={theme.palette.grey[500]}
        sx={{
          gap: 4,
        }}
      >
        <CircularProgress size={18} color={theme.palette.grey[500]} />
        {t('resources.invoice.waitingTransfer')}
      </Box>
    );
  }

  return null;
});

const useStyles = makeStyles({
  root: {
    width: '100%',
    justifyContent: 'center',
    "& [class*='MuiStepConnector']": {
      maxWidth: 100,
    },
  },
  dialog: {
    zIndex: '1000 !important',
  },
});
